import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ButtonSmall from "@/common/ButtonSmall/ButtonSmall";
import ConfirmSubmitButton from "@/common/ConfirmSubmitButton/ConfirmSubmitButton";
import Text from "@/common/Text/Text";
import {
  useAssignZoneChannelMutation,
  useGetZoneChannelAssignmentQuery,
  useLazyGetZoneChannelAssignmentQuery,
} from "@/features/devices/devicesApi";
import {
  confirmedChannelLabel,
  isDeviceConfirmedForAssignment,
  requestedVsConfirmedCopy,
  type ZoneChannelAssignmentSnapshot,
} from "@/features/devices/zoneChannelAssignment";
import { SyncOutlined } from "@ant-design/icons";
import { Select } from "antd";
import toast from "react-hot-toast";
import ControlPanelActionRow from "../ControlPanelActionRow/ControlPanelActionRow";
import styles from "./MusicChannelPanel.module.scss";

const CONFIRM_POLL_MS = 2000;
const CONFIRM_POLL_MAX_MS = 45000;

type Props = {
  zoneId: string | null;
  deviceId: string | null;
  commandsEnabled: boolean;
  consoleOpen?: boolean;
  deviceAppearsOffline?: boolean;
};

type ApplyPhase = "idle" | "sending" | "awaiting_ack" | "failed";

const MusicChannelPanel: React.FC<Props> = ({
  zoneId,
  deviceId,
  commandsEnabled,
  consoleOpen = false,
  deviceAppearsOffline = false,
}) => {
  const skip = !zoneId || !consoleOpen;
  const { data, isFetching, refetch, error } = useGetZoneChannelAssignmentQuery(
    { zoneId: zoneId || "", includeAuthorized: true },
    { skip, pollingInterval: 0 }
  );
  const [fetchAssignment] = useLazyGetZoneChannelAssignmentQuery();
  const [assignZoneChannel, { isLoading: isAssigning }] =
    useAssignZoneChannelMutation();

  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [confirmArmed, setConfirmArmed] = useState(false);
  const [phase, setPhase] = useState<ApplyPhase>("idle");
  const [pendingRequest, setPendingRequest] = useState<{
    channelId: string;
    channelName: string | null;
  } | null>(null);
  const [lastSnapshot, setLastSnapshot] =
    useState<ZoneChannelAssignmentSnapshot | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    setSelectedChannelId(null);
    setConfirmArmed(false);
    setPhase("idle");
    setPendingRequest(null);
    return () => {
      cancelledRef.current = true;
    };
  }, [zoneId, deviceId]);

  useEffect(() => {
    if (data) setLastSnapshot(data);
  }, [data]);

  const snapshot = data || lastSnapshot;
  const authorized = snapshot?.authorizedChannels || [];
  const deviceConfirmed = isDeviceConfirmedForAssignment(snapshot, deviceId);
  const confirmedLabel = confirmedChannelLabel(snapshot, deviceId);

  const options = useMemo(
    () =>
      authorized.map((c) => ({
        value: c.channelId,
        label: c.name?.trim()
          ? `${c.name.trim()} (${c.channelId})`
          : c.channelId,
      })),
    [authorized]
  );

  const currentAssignedId = (snapshot?.channelId || "").trim() || null;
  const selectionDiffers =
    !!selectedChannelId && selectedChannelId !== currentAssignedId;
  const applyInFlight = phase === "sending" || phase === "awaiting_ack";
  const dirty = selectionDiffers && !applyInFlight;

  const statusText = useMemo(() => {
    if (!zoneId) return "Select a device with a Zone to manage music channel.";
    if (error) return "Could not load Zone channel assignment.";
    if (phase === "sending") return "Saving assignment and notifying device…";
    if (phase === "awaiting_ack") {
      return (
        requestedVsConfirmedCopy({
          requestedChannelId: pendingRequest?.channelId || null,
          requestedChannelName: pendingRequest?.channelName,
          snapshot,
          deviceId,
        }) || "Waiting for device acknowledgment…"
      );
    }
    if (phase === "failed") {
      return (
        requestedVsConfirmedCopy({
          requestedChannelId: pendingRequest?.channelId || null,
          requestedChannelName: pendingRequest?.channelName,
          snapshot,
          deviceId,
        }) || "Channel change was not confirmed. Confirmed channel unchanged."
      );
    }
    if (deviceAppearsOffline) {
      return `Confirmed channel: ${confirmedLabel} (device appears offline)`;
    }
    if (!deviceConfirmed) {
      const assigned = snapshot?.channelName || snapshot?.channelId || "none";
      return `Assigned (not device-confirmed): ${assigned}`;
    }
    return `Confirmed channel: ${confirmedLabel}`;
  }, [
    zoneId,
    error,
    phase,
    pendingRequest,
    snapshot,
    deviceId,
    deviceAppearsOffline,
    deviceConfirmed,
    confirmedLabel,
  ]);

  const pollUntilDeviceConfirmed = useCallback(
    async (requestedChannelId: string): Promise<boolean> => {
      const started = Date.now();
      while (Date.now() - started < CONFIRM_POLL_MAX_MS) {
        if (cancelledRef.current) return false;
        await new Promise((r) => setTimeout(r, CONFIRM_POLL_MS));
        if (cancelledRef.current || !zoneId) return false;
        try {
          const next = await fetchAssignment({
            zoneId,
            includeAuthorized: true,
          }).unwrap();
          setLastSnapshot(next);
          if (
            isDeviceConfirmedForAssignment(next, deviceId) &&
            (next.channelId || "").trim() === requestedChannelId
          ) {
            return true;
          }
        } catch {
          // keep polling until timeout
        }
      }
      return false;
    },
    [deviceId, fetchAssignment, zoneId]
  );

  const handleApply = async () => {
    if (!commandsEnabled || !zoneId || !selectedChannelId || !selectionDiffers) {
      return;
    }
    if (isAssigning || applyInFlight) return;

    const option = authorized.find((c) => c.channelId === selectedChannelId);
    if (!option) {
      toast.error("Select an authorized channel");
      return;
    }

    const expectedRevision =
      typeof snapshot?.assignmentRevision === "number"
        ? snapshot.assignmentRevision
        : null;
    const requested = {
      channelId: selectedChannelId,
      channelName: option.name || null,
    };
    setPendingRequest(requested);
    setPhase("sending");
    setConfirmArmed(false);

    try {
      const put = await assignZoneChannel({
        zoneId,
        channelId: selectedChannelId,
        expectedRevision,
      }).unwrap();
      setLastSnapshot(put);

      if (
        isDeviceConfirmedForAssignment(put, deviceId) &&
        (put.channelId || "").trim() === selectedChannelId
      ) {
        setPhase("idle");
        setPendingRequest(null);
        setSelectedChannelId(null);
        toast.success("Music channel confirmed on device");
        return;
      }

      setPhase("awaiting_ack");
      const ok = await pollUntilDeviceConfirmed(selectedChannelId);
      if (cancelledRef.current) return;
      if (ok) {
        setPhase("idle");
        setPendingRequest(null);
        setSelectedChannelId(null);
        toast.success("Music channel confirmed on device");
      } else {
        // Never treat requested as applied; restore confirmed view via GET.
        setPhase("failed");
        try {
          await refetch();
        } catch {
          /* keep lastSnapshot */
        }
        toast.error(
          "Device did not confirm the channel change. Confirmed channel unchanged."
        );
      }
    } catch (err: unknown) {
      setPhase("failed");
      const msg =
        err && typeof err === "object" && "data" in err
          ? String(
              (err as { data?: { message?: string; code?: string } }).data
                ?.message ||
                (err as { data?: { code?: string } }).data?.code ||
                "Assignment failed"
            )
          : "Assignment failed";
      toast.error(msg);
      try {
        await refetch();
      } catch {
        /* ignore */
      }
    }
  };

  const handleRetryNotify = async () => {
    if (!commandsEnabled || !zoneId || !currentAssignedId) return;
    setPhase("sending");
    try {
      const put = await assignZoneChannel({
        zoneId,
        channelId: currentAssignedId,
        expectedRevision: snapshot?.assignmentRevision ?? null,
        retryNotify: true,
      }).unwrap();
      setLastSnapshot(put);
      setPhase("awaiting_ack");
      setPendingRequest({
        channelId: currentAssignedId,
        channelName: snapshot?.channelName || null,
      });
      const ok = await pollUntilDeviceConfirmed(currentAssignedId);
      if (cancelledRef.current) return;
      setPhase(ok ? "idle" : "failed");
      if (ok) {
        setPendingRequest(null);
        toast.success("Retry notify confirmed");
      } else {
        toast.error("Retry notify did not receive device ACK");
      }
    } catch {
      setPhase("failed");
      toast.error("Retry notify failed");
    }
  };

  if (!zoneId) {
    return (
      <div className={styles.wrap} data-testid="music-channel-panel-disabled">
        <Text size="md">Music Channel</Text>
        <Text size="sm">{statusText}</Text>
      </div>
    );
  }

  return (
    <div className={styles.wrap} data-testid="music-channel-panel">
      <ControlPanelActionRow
        borderBottom={false}
        leftComponent={
          <div>
            <Text size="md">Music Channel</Text>
            <Text size="sm" className={styles.status}>
              {statusText}
              {(isFetching || isAssigning || phase === "awaiting_ack") && (
                <SyncOutlined className={styles.syncLoader} spin />
              )}
            </Text>
            {snapshot?.notifyStatus === "pending" && (
              <Text size="sm" className={styles.pendingHint}>
                Notify pending — use Retry notify if the device missed delivery.
              </Text>
            )}
          </div>
        }
        rightComponent={
          <div className={styles.controls}>
            <Select
              className={styles.select}
              placeholder="Authorized channels only"
              value={selectedChannelId ?? undefined}
              options={options}
              disabled={
                !commandsEnabled ||
                options.length === 0 ||
                phase === "sending" ||
                phase === "awaiting_ack"
              }
              onChange={(value) => {
                setSelectedChannelId(value);
                setConfirmArmed(false);
                if (phase === "failed") setPhase("idle");
              }}
              showSearch
              optionFilterProp="label"
              data-testid="music-channel-select"
            />
            <ConfirmSubmitButton
              disabled={!commandsEnabled || !dirty}
              id="music-channel-apply"
              isActive={confirmArmed && dirty}
              onClick={() => setConfirmArmed((v) => !v)}
              onSubmit={handleApply}
              text="Apply"
            />
            {snapshot?.notifyStatus === "pending" && (
              <ButtonSmall
                disabled={!commandsEnabled || phase === "sending"}
                onClick={handleRetryNotify}
              >
                Retry notify
              </ButtonSmall>
            )}
          </div>
        }
      />
    </div>
  );
};

export default MusicChannelPanel;
